import * as Tone from 'tone';
import { YuMode } from '../components/ModeSelector';

/**
 * Audio Manager for synchronized sound effects
 * 
 * Uses Tone.js for high-quality real-time synthesis.
 * Supports multiple modes with different scales and instruments.
 */
export class AudioManager {
    private synth: Tone.PolySynth | null = null;
    private reverb: Tone.Reverb | null = null;
    private isInitialized = false;
    private lastTriggerTime: number = 0;
    private minTriggerInterval = 50; // Minimum ms between triggers
    private currentMode: YuMode = 'curious'; // Default mode

    // Scale Definitions (Descending)
    private scales: Record<YuMode, string[]> = {
        // D Minor Blues: D, C, A, Ab, G, F, D
        'fruity': ['D', 'C', 'A', 'Ab', 'G', 'F', 'D'],

        // E Minor Natural: E, D, C, B, A, G, F#, E
        'melancholic': ['E', 'D', 'C', 'B', 'A', 'G', 'F#', 'E'],

        // E Minor Pentatonic: E, D, B, A, G, E
        'mysterious': ['E', 'D', 'B', 'A', 'G', 'E'],

        // E Major Pentatonic: E, C#, B, G#, F#, E
        'curious': ['E', 'C#', 'B', 'G#', 'F#', 'E']
    };

    constructor() {
        // We initialize lazily on the first user interaction
    }

    /**
     * Set the current audio mode
     */
    public setMode(mode: YuMode): void {
        if (this.currentMode === mode) return;

        this.currentMode = mode;
        console.log(`[AudioManager] Switched to mode: ${mode}`);

        // Update synth sound based on mode
        this.updateSynthForMode(mode);
    }

    /**
     * Initialize Tone.js context and instruments
     */
    public async initialize(): Promise<void> {
        if (this.isInitialized) return;

        await Tone.start();
        console.log('[AudioManager] Tone.js Context Started');

        // Create a Reverb effect
        this.reverb = new Tone.Reverb({
            decay: 3.5,
            preDelay: 0.1,
            wet: 0.4
        }).toDestination();

        await this.reverb.generate();

        // Create initial synth
        this.synth = new Tone.PolySynth(Tone.Synth).connect(this.reverb);

        // Apply initial mode settings
        this.updateSynthForMode(this.currentMode);

        this.isInitialized = true;
        console.log('[AudioManager] Synth and Reverb initialized');
    }

    /**
     * Update synth parameters based on mode
     */
    private updateSynthForMode(mode: YuMode): void {
        if (!this.synth) return;

        this.synth.releaseAll();

        switch (mode) {
            case 'fruity':
                // Piano-ish (Triangle)
                this.synth.set({
                    oscillator: { type: "triangle" },
                    envelope: { attack: 0.02, decay: 0.3, sustain: 0.4, release: 1.2 },
                    volume: -5
                });
                break;

            case 'melancholic':
                // Violin-ish (Sawtooth, slow attack)
                this.synth.set({
                    oscillator: { type: "sawtooth" },
                    envelope: { attack: 0.1, decay: 0.3, sustain: 0.8, release: 1.5 },
                    volume: -8
                });
                break;

            case 'mysterious':
                // Sitar-ish (FM/AM or just a brighter pulse)
                // Using a pulse wave with some modulation if possible, or just a sharp square
                this.synth.set({
                    oscillator: { type: "square" }, // Square is hollow/woody
                    envelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 1.0 },
                    volume: -10
                });
                break;

            case 'curious':
                // Flute-ish (Sine, breathy)
                this.synth.set({
                    oscillator: { type: "sine" },
                    envelope: { attack: 0.05, decay: 0.1, sustain: 0.7, release: 1.0 },
                    volume: -2
                });
                break;
        }
    }

    /**
     * Preload (No-op)
     */
    public preload(_id: string, _filepath: string): void {
        // No-op
    }

    /**
     * Play a note based on step index and current mode
     */
    public async play(_id: string, config: any): Promise<void> {
        // Ensure audio is initialized before playing
        if (!this.isInitialized || !this.synth) {
            await this.initialize().catch(e => console.error('[AudioManager] Initialization failed:', e));
            // Double-check after initialization attempt
            if (!this.synth) {
                console.warn('[AudioManager] Synth not available, skipping playback');
                return;
            }
        }

        const now = Date.now();
        if (now - this.lastTriggerTime < this.minTriggerInterval) {
            return;
        }
        this.lastTriggerTime = now;

        // Get step index and character from config
        const stepIndex = config.stepIndex || 0;
        const character = config.character || 'main';
        const baseVolume = config.volume !== undefined ? config.volume : 0.5;

        // Calculate note based on mode's scale
        const scale = this.scales[this.currentMode];
        const note = this.getNoteForStep(stepIndex, character, scale);

        // Adjust volume based on mode (some synths are louder)
        const velocity = Math.min(Math.max(baseVolume, 0), 1);

        this.synth.triggerAttackRelease(note, "8n", undefined, velocity);
        console.log(`[AudioManager] Mode: ${this.currentMode} | Note: ${note} | Step: ${stepIndex}`);
    }

    /**
     * Calculate the specific note for a step index
     */
    private getNoteForStep(stepIndex: number, character: string, scale: string[]): string {
        // Determine octave range based on character
        let startOctave = 6;
        let minOctave = 3;

        if (character === 'person1') {
            startOctave = 5;
            minOctave = 3;
        }
        if (character === 'person2') {
            startOctave = 4;
            minOctave = 2;
        }

        const scaleLength = scale.length;
        const noteIndex = stepIndex % scaleLength;
        const octaveDrop = Math.floor(stepIndex / scaleLength);

        // Wrap octaves to stay in audible range
        // Range size is (start - min + 1)
        // e.g., 6 to 3 is size 4 (6, 5, 4, 3)
        const rangeSize = startOctave - minOctave + 1;
        const octave = startOctave - (octaveDrop % rangeSize);

        const noteName = scale[noteIndex];
        return `${noteName}${octave}`;
    }

    /**
     * Stop all sounds
     */
    public stopAll(): void {
        if (this.synth) {
            this.synth.releaseAll();
        }
    }

    /**
     * Dispose resources
     */
    public dispose(): void {
        if (this.synth) {
            this.synth.dispose();
            this.synth = null;
        }
        if (this.reverb) {
            this.reverb.dispose();
            this.reverb = null;
        }
        this.isInitialized = false;
    }
}

// Singleton instance
export const audioManager = new AudioManager();
